/**
 * Pedido por WhatsApp y cupón de vendedor.
 *
 * El cupón es el código del link del vendedor en mayúsculas (YORDA). Da un
 * descuento fijo en USD sobre toda la compra si el carrito tiene algún kit
 * solar o si el total pasa del mínimo (3000 USD por defecto). Ambos valores
 * salen de Config_General en el Sheet.
 */
import type { Product } from '@/types';
import type { CuponConfig, Vendedor } from '@/services/store.service';

export interface LineaPedido {
  producto: Product;
  cantidad: number;
}

export interface ResultadoCupon {
  codigo: string;
  vendedor: Vendedor;
  /** Si la compra cumple las condiciones (kit o más del mínimo) */
  aplica: boolean;
  /** Descuento en USD (0 si no aplica) */
  descuento: number;
  config: CuponConfig;
}

const sinTildes = (t: string) => t.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '');

/** Kit solar: su categoría o su nombre dicen "kit". */
export function esKit(p: Product): boolean {
  return /\bkits?\b/.test(sinTildes(`${p.categoryId.replace(/-/g, ' ')} ${p.name}`));
}

export const subtotalDe = (lineas: LineaPedido[]) =>
  lineas.reduce((s, l) => s + l.producto.price * l.cantidad, 0);

/** Busca el vendedor dueño de un código de cupón. */
export function vendedorDelCupon(codigo: string, vendedores: Vendedor[]): Vendedor | null {
  const c = codigo.trim().toLowerCase();
  return vendedores.find((v) => v.codigo.toLowerCase() === c) ?? null;
}

export function calculaCupon(
  codigo: string | null,
  vendedores: Vendedor[],
  config: CuponConfig | null,
  lineas: LineaPedido[],
): ResultadoCupon | null {
  if (!codigo || !config) return null;
  const vendedor = vendedorDelCupon(codigo, vendedores);
  if (!vendedor) return null;
  const subtotal = subtotalDe(lineas);
  const aplica = lineas.some((l) => esKit(l.producto)) || subtotal > config.minimoUsd;
  return {
    codigo: codigo.toUpperCase(),
    vendedor,
    aplica,
    descuento: aplica ? Math.min(config.descuentoUsd, subtotal) : 0,
    config,
  };
}

const usd = (n: number) => `$${n.toFixed(2)} USD`;

/** Texto del pedido para WhatsApp, ya codificado para la URL. */
export function mensajePedido(
  saludo: string,
  lineas: LineaPedido[],
  cupon: ResultadoCupon | null,
  extra: string[] = [],
): string {
  const subtotal = subtotalDe(lineas);
  const partes = [
    saludo,
    '',
    ...lineas.map((l) => `• ${l.producto.name} x${l.cantidad} — ${usd(l.producto.price * l.cantidad)}`),
    '',
  ];
  if (cupon?.aplica) {
    partes.push(
      `Subtotal: ${usd(subtotal)}`,
      `Cupón ${cupon.codigo}: −${usd(cupon.descuento)}`,
      `Total: ${usd(subtotal - cupon.descuento)}`,
    );
  } else {
    partes.push(`Total: ${usd(subtotal)}`);
  }
  partes.push(...extra, '', '¿Me pueden confirmar disponibilidad y coordinar el envío?');
  return encodeURIComponent(partes.join('\n'));
}
